/**
 * `src/lib/ui/nav/nav-model.ts` — where every screen sits, which tab it lights,
 * where its back control goes, and which motion a navigation gets.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  PURE, AND THAT IS THE POINT
 * ═══════════════════════════════════════════════════════════════════════════
 * No `$app`, no `svelte`, no DOM. Every decision the navigation makes is a
 * function of a route id, a small history mirror and a few facts about the
 * navigation, so it is tested in vitest's node project over plain objects
 * (`nav-model.spec.ts`). `nav.svelte.ts` is the thin runtime that feeds it
 * from Kit's navigation hooks and holds the result in `$state`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE ROUTE TABLE
 * ═══════════════════════════════════════════════════════════════════════════
 * Each route names its tab, whether it IS that tab's root, its parents and the
 * static title a back control shows when it points here.
 *
 *   - `parents[0]` is the UP target on a cold entry, and it is always one of
 *     `UP_TARGETS`: a static route with a title, so "up" never needs params.
 *     The other parents only matter for HISTORY mode: when the previous screen
 *     is any of them, back is `history.back()` and lands where the person was.
 *   - The reader is strongly in the Read tab. A bookmark opened from العلامات
 *     lights المصحف; back still returns to العلامات, through history.
 *   - Only the three shared screens (`/search`, `/qibla`, `/salah/settings`)
 *     INHERIT the tab they were opened from, so the Home Qibla tile keeps
 *     الرئيسية lit and search from the reader keeps المصحف lit. Opened cold,
 *     they fall back to `defaultTab`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE HISTORY MIRROR
 * ═══════════════════════════════════════════════════════════════════════════
 * The browser will not say what is behind the current entry, so the app keeps
 * its own copy, driven from `afterNavigate`: one entry per history entry, the
 * index tracking the real position. An entry's `kind` is the motion it was
 * reached with going FORWARD, so going back from it plays the inverse.
 *
 * After a reload the mirror holds one entry. Everything then degrades to "up"
 * mode and default tabs: correct, only less precise.
 */

export type TabId = 'home' | 'read' | 'salah' | 'bookmarks';

/** `push` / `pop`: the shared-axis shift. `fade`: fade-through (tab switches,
 *  every arrival in the reader). `none`: an instant swap. */
export type NavKind = 'push' | 'pop' | 'fade' | 'none';

export interface RouteInfo {
	/** The tab this route lights, or `inherit` the tab it was opened from. */
	tab: TabId | 'inherit';
	/** For `inherit` routes: the tab on a cold entry. */
	defaultTab?: TabId;
	/** The route IS its tab's root: no back control, and reselecting the tab
	 *  scrolls to the top instead of navigating. */
	root?: true;
	/** `parents[0]` is the up target; any of them enables history mode. */
	parents: readonly string[];
	/** What a back control pointing HERE says, when the page registered none.
	 *  Absent for param routes, whose title only exists at runtime. */
	title?: string;
}

export const TAB_ROOTS: Record<TabId, string> = {
	home: '/',
	read: '/read',
	salah: '/salah',
	bookmarks: '/bookmarks'
};

export const ROUTES: Record<string, RouteInfo> = {
	'/': { tab: 'home', root: true, parents: [], title: 'الرئيسية' },
	'/read': { tab: 'read', root: true, parents: [], title: 'فهرس السور' },
	'/salah': { tab: 'salah', root: true, parents: [], title: 'الصلاة' },
	'/bookmarks': { tab: 'bookmarks', root: true, parents: [], title: 'العلامات' },
	'/read/[surah]': { tab: 'read', parents: ['/read', '/bookmarks', '/search'] },
	'/settings': { tab: 'home', parents: ['/'], title: 'الإعدادات' },
	'/about': { tab: 'home', parents: ['/settings'], title: 'عن التطبيق' },
	'/azkar': { tab: 'home', parents: ['/'], title: 'الأذكار' },
	'/azkar/[set]': { tab: 'home', parents: ['/azkar', '/'] },
	'/tasbih': { tab: 'home', parents: ['/'], title: 'المسبحة' },
	'/tasbih/[routine]': { tab: 'home', parents: ['/tasbih'] },
	'/search': {
		tab: 'inherit',
		defaultTab: 'home',
		parents: ['/', '/read/[surah]', '/read'],
		title: 'البحث في القرآن'
	},
	'/qibla': { tab: 'inherit', defaultTab: 'salah', parents: ['/salah', '/'], title: 'القبلة' },
	'/salah/settings': {
		tab: 'inherit',
		defaultTab: 'salah',
		parents: ['/salah', '/settings'],
		title: 'إعدادات المواقيت'
	}
};

/** A null route id (the 404 page) or one the table does not know. */
export const UNKNOWN_ROUTE: RouteInfo = { tab: 'home', parents: ['/'] };

/** Every route an up-navigation can land on. Static, so `nav.svelte.ts` can
 *  build each href through a `resolve()` helper without params. */
export const UP_TARGETS = [
	'/',
	'/read',
	'/salah',
	'/bookmarks',
	'/settings',
	'/azkar',
	'/tasbih'
] as const;

export function routeInfo(route: string | null): RouteInfo {
	return (route === null ? undefined : ROUTES[route]) ?? UNKNOWN_ROUTE;
}

/** The tab a route lights when opened from `openedFrom` (null: a cold entry). */
export function tabFor(route: string | null, openedFrom: TabId | null): TabId {
	const info = routeInfo(route);
	if (info.tab !== 'inherit') return info.tab;
	return openedFrom ?? info.defaultTab ?? 'home';
}

export interface NavEntry {
	route: string | null;
	/** pathname + search */
	href: string;
	tab: TabId;
	/** How this entry was reached going forward. */
	kind: NavKind;
}

export interface NavStack {
	entries: readonly NavEntry[];
	index: number;
}

export const EMPTY_STACK: NavStack = { entries: [], index: -1 };

export function current(stack: NavStack): NavEntry | undefined {
	return stack.entries[stack.index];
}

export type StackEvent =
	| { type: 'enter'; route: string | null; href: string }
	| { type: 'push'; route: string | null; href: string; kind: NavKind; replace: boolean }
	| { type: 'popstate'; route: string | null; href: string; delta: number; kind: NavKind };

/** The mirror's reducer. Pure: always returns a new stack. */
export function reduce(stack: NavStack, ev: StackEvent): NavStack {
	if (ev.type === 'enter') {
		return {
			entries: [{ route: ev.route, href: ev.href, tab: tabFor(ev.route, null), kind: 'none' }],
			index: 0
		};
	}

	if (ev.type === 'push') {
		const entry: NavEntry = {
			route: ev.route,
			href: ev.href,
			tab: tabFor(ev.route, current(stack)?.tab ?? null),
			kind: ev.kind
		};
		if (stack.index < 0) return { entries: [entry], index: 0 };
		if (ev.replace) {
			/* `replaceState` keeps the forward entries, so the mirror does too.
			 *
			 * The slot's kind is NOT the replacing navigation's own. An up-
			 * navigation arrives with `pop`, but that describes leaving the
			 * replaced screen, which is no longer in history: the slot is now
			 * reached from the entry BEHIND it. Home → reader → «فهرس السور»
			 * stored `pop`, so the system back to Home slid forward (`push`) and
			 * forward again slid back, where every other change of tab fades. */
			const entries = stack.entries.slice();
			entries[stack.index] = { ...entry, kind: arrivalKind(stack.entries[stack.index - 1], entry) };
			return { entries, index: stack.index };
		}
		return { entries: [...stack.entries.slice(0, stack.index + 1), entry], index: stack.index + 1 };
	}

	/*
	 * popstate. An entry first seen by going BACK was reached some way the
	 * mirror never saw; it is recorded as `push`, the common case, so going
	 * back from it again plays `pop`, which is what rule 7 says for an unknown
	 * entry anyway. Going forward, the arrival kind is the real one.
	 */
	const index = stack.index + ev.delta;
	const unknown: NavEntry = {
		route: ev.route,
		href: ev.href,
		tab: tabFor(ev.route, null),
		kind: ev.delta > 0 ? ev.kind : 'push'
	};
	const there = stack.index < 0 ? undefined : stack.entries[index];
	if (!there) return { entries: [unknown], index: 0 };
	const entries = stack.entries.slice();
	entries[index] = there.route === ev.route ? { ...there, href: ev.href } : unknown;
	return { entries, index };
}

/**
 * The motion a plain forward link from `prev` to `next` would get: `classify`'s
 * rules 3, 4, 5, 9 and 10, for a link that is neither a tab nor our own up-
 * navigation. `none` with nothing behind (the first entry is never animated
 * into).
 */
export function arrivalKind(prev: NavEntry | undefined, next: NavEntry): NavKind {
	if (!prev) return 'none';
	if (prev.href === next.href) return 'none';
	if (next.route === '/read/[surah]') return 'fade';
	if (prev.route === next.route) return 'none';
	return prev.tab !== next.tab ? 'fade' : 'push';
}

/**
 * The current entry's href after a SHALLOW `replaceState` (the search page
 * mirrors its query into the address bar without a navigation, so no
 * `afterNavigate` records it).
 *
 * Only when the top IS `route`: in the gap before `afterNavigate` records a
 * new page, the top is still the screen being left, and its href must not be
 * overwritten with the new page's address. `afterNavigate` records that one.
 */
export function withCurrentHref(stack: NavStack, route: string | null, href: string): NavStack {
	const top = current(stack);
	if (!top || top.route !== route || top.href === href) return stack;
	const entries = stack.entries.slice();
	entries[stack.index] = { ...top, href };
	return { entries, index: stack.index };
}

/** The motion that undoes `kind`. `none` inverts to `pop`: leaving an entry
 *  whose arrival was not animated (the first one) is still going back. */
export function invert(kind: NavKind): NavKind {
	switch (kind) {
		case 'push':
			return 'pop';
		case 'pop':
			return 'push';
		case 'fade':
			return 'fade';
		case 'none':
			return 'pop';
	}
}

export interface NavPoint {
	route: string | null;
	/** pathname + search */
	href: string;
}

export interface ClassifyInput {
	type: 'enter' | 'form' | 'leave' | 'link' | 'goto' | 'popstate';
	from: NavPoint | null;
	to: NavPoint | null;
	willUnload: boolean;
	/** popstate only; 0 otherwise. */
	delta: number;
	/** `PopStateEvent.hasUAVisualTransition`: the browser already animated it. */
	uaVisual: boolean;
	/** `pop` while our own `goBack()` up-navigation is in flight. */
	intent: 'pop' | null;
	/** The clicked link's `data-nav` (TabBar's links carry `tab`). */
	linkNav: string | null;
	stack: NavStack;
}

/** Which motion a navigation gets. First match wins (spec §8.2). */
export function classify(i: ClassifyInput): NavKind {
	const { from, to } = i;
	// 1. Nothing to animate between, or not ours to animate.
	if (!from || !to || i.willUnload) return 'none';
	if (i.type === 'enter' || i.type === 'form' || i.type === 'leave') return 'none';
	// 2. Safari's swipe-back and Chrome's predictive back already drew one.
	if (i.type === 'popstate' && i.uaVisual) return 'none';
	// 3. Same page.
	if (from.href === to.href) return 'none';
	// 4. Every arrival in the reader fades: its restore settles at opacity 0.
	if (to.route === '/read/[surah]') return 'fade';
	// 5. Same screen, new data (search `?q=`, routine to routine).
	if (from.route === to.route) return 'none';
	// 6. Our own up-navigation.
	if (i.intent === 'pop') return 'pop';
	// 7. Back and forward replay the recorded motion.
	if (i.type === 'popstate') {
		const leaving = current(i.stack);
		if (!leaving) return i.delta < 0 ? 'pop' : 'push';
		if (i.delta === -1) return invert(leaving.kind);
		if (i.delta < 0) return 'pop';
		return i.stack.entries[i.stack.index + i.delta]?.kind ?? 'push';
	}
	const curTab = current(i.stack)?.tab ?? tabFor(from.route, null);
	const toTab = tabFor(to.route, curTab);
	// 8. A tab: reselecting goes up to its root; another tab fades through.
	if (i.linkNav === 'tab') return toTab === curTab ? 'pop' : 'fade';
	// 9. Anything else that changes the tab.
	if (toTab !== curTab) return 'fade';
	// 10. Deeper in the same tab.
	return 'push';
}

export type BackTarget =
	{ mode: 'history'; route: string | null; href: string } | { mode: 'up'; route: string };

/**
 * Where the back control on `route` goes. `null` on a tab root.
 *
 * HISTORY mode when the screen we came from is one of this route's parents:
 * `history.back()` lands exactly there, scroll restored. UP mode otherwise (a
 * cold deep link, a reload, arriving from another tab or a non-parent): a
 * replacing navigation to `parents[0]`. Android's system back stays
 * chronological; this control is hierarchical.
 *
 * THE GAP. Between Kit rendering the new page and `afterNavigate` recording
 * it, the mirror's top is still the screen we came from. A top whose route is
 * not `route` IS that screen, so the label is right while the page renders.
 */
export function backTarget(stack: NavStack, route: string | null): BackTarget | null {
	const info = routeInfo(route);
	if (info.root) return null;
	const top = current(stack);
	const prev = top && top.route !== route ? top : stack.entries[stack.index - 1];
	if (prev && prev.route !== null && info.parents.includes(prev.route)) {
		return { mode: 'history', route: prev.route, href: prev.href };
	}
	return { mode: 'up', route: info.parents[0] ?? '/' };
}
